import { useSyncExternalStore } from 'react';
import { TABLET_MIN_SHORT_SIDE_CSS_PX } from 'utils/formFactor';

/**
 * True on a phone driven by touch: the native iPhone/Android apps and phone browsers.
 * False on tablets and desktops.
 *
 * Drawer rule (EN-11291, 2026-10-24): on a phone, modal prompts open as a bottom drawer;
 * a centered pop-up is used only on tablet and desktop. This hook is that switch.
 *
 * "Phone" is the platforms' own line (`utils/formFactor`: shortest screen side under
 * 600 CSS px). A hover-capable pointer is never treated as a phone, and a screen that
 * reports no size (test environments) counts as unknown, which keeps the centered modal.
 * The verdict is live: a foldable that unfolds becomes a tablet on the next resize.
 */
const subscribe = (onChange: () => void) => {
  window.addEventListener('resize', onChange);
  window.addEventListener('orientationchange', onChange);
  return () => {
    window.removeEventListener('resize', onChange);
    window.removeEventListener('orientationchange', onChange);
  };
};

export const isPhoneTouch = (): boolean => {
  if (typeof window === 'undefined') return false;
  const shortSide = Math.min(window.screen?.width ?? 0, window.screen?.height ?? 0);
  // `!(> 0)` also catches NaN, which `<= 0` lets through as a phone.
  if (!(shortSide > 0) || shortSide >= TABLET_MIN_SHORT_SIDE_CSS_PX) return false;
  return !(window.matchMedia?.('(hover: hover)')?.matches ?? false);
};

export const useIsPhoneTouch = (): boolean =>
  useSyncExternalStore(subscribe, isPhoneTouch, () => false);
